"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import type { AuthChangeEvent, Session } from "@supabase/supabase-js"
import { getSupabaseBrowserClient } from "@/utils/supabase/client"
import { getGuestId } from "@/lib/guest"
import {
  fetchSearchHistory,
  syncSearchHistoryLogin,
  syncSearchHistoryLogout,
  postSearchHistoryTerm,
  deleteSearchHistoryTerm,
  clearSearchHistory as clearSearchHistoryApi,
  type SearchHistoryRow,
} from "@/lib/search/search-history-api"

const MAX_HISTORY_ITEMS = 10
const CACHE_TTL = 2000

let _searchHistorySupabase: ReturnType<typeof getSupabaseBrowserClient> | null = null
function getSearchHistoryClient() {
  if (!_searchHistorySupabase) _searchHistorySupabase = getSupabaseBrowserClient()
  return _searchHistorySupabase
}

let globalSyncInProgress = false
let globalHistory: string[] = []
let globalHistoryLoaded = false
let globalLoadPromise: Promise<void> | null = null
let globalLastFetch = 0
const historyListeners = new Set<(items: string[]) => void>()

const normalizeTerm = (term: string) => term.trim().toLowerCase()

const broadcastHistory = (items: string[]) => {
  globalHistory = items
  globalHistoryLoaded = true
  historyListeners.forEach((listener) => listener(items))
}

const subscribeHistory = (listener: (items: string[]) => void) => {
  historyListeners.add(listener)
  return () => {
    historyListeners.delete(listener)
  }
}

const dedupeRows = (rows: SearchHistoryRow[]) => {
  const sorted = rows
    .slice()
    .sort(
      (a, b) =>
        new Date(b.updated_at ?? 0).getTime() -
        new Date(a.updated_at ?? 0).getTime()
    )

  const seen = new Set<string>()
  const terms: string[] = []

  for (const row of sorted) {
    const raw = row.term ?? ""
    const key = normalizeTerm(row.term_normalized ?? raw)
    if (!key || seen.has(key)) continue
    seen.add(key)
    terms.push(raw)
    if (terms.length >= MAX_HISTORY_ITEMS) break
  }

  return terms
}

const resolveUserId = async (lastUserIdRef: React.MutableRefObject<string | null>) => {
  if (lastUserIdRef.current) return lastUserIdRef.current
  const {
    data: { session },
  } = await getSearchHistoryClient().auth.getSession()
  const uid = session?.user?.id ?? null
  if (uid) lastUserIdRef.current = uid
  return uid
}

const loadHistory = async (
  userId: string | null,
  guestId: string,
  force = false
) => {
  const now = Date.now()
  if (!force && globalHistoryLoaded && now - globalLastFetch < CACHE_TTL) {
    return
  }

  if (globalLoadPromise && !force) {
    await globalLoadPromise
    return
  }

  const task = (async () => {
    const rows = await fetchSearchHistory({ userId, guestId })
    const terms = dedupeRows(rows)
    globalLastFetch = Date.now()
    broadcastHistory(terms)
  })()

  globalLoadPromise = task
  try {
    await task
  } finally {
    if (globalLoadPromise === task) globalLoadPromise = null
  }
}

export function useSearchHistory() {
  const [searchHistory, setSearchHistory] = useState<string[]>(globalHistory)
  const [isInitialized, setIsInitialized] = useState(globalHistoryLoaded)
  const lastUserId = useRef<string | null>(null)

  useEffect(() => {
    const unsubscribe = subscribeHistory((items) => {
      setSearchHistory(items)
      setIsInitialized(true)
    })
    return unsubscribe
  }, [])

  const refreshHistory = useCallback(async (force = false) => {
    const guestId = getGuestId()
    await loadHistory(lastUserId.current, guestId, force)
    setIsInitialized(true)
  }, [])

  useEffect(() => {
    const handleLogin = async (uid: string | null) => {
      if (!uid || globalSyncInProgress) return
      if (lastUserId.current === uid) {
        await loadHistory(uid, getGuestId())
        return
      }
      globalSyncInProgress = true
      try {
        const guestId = getGuestId()
        await syncSearchHistoryLogin({ userId: uid, guestId })
        lastUserId.current = uid
        await loadHistory(uid, guestId, true)
      } finally {
        globalSyncInProgress = false
      }
    }

    const handleLogout = async () => {
      const uid = lastUserId.current
      if (!uid || globalSyncInProgress) {
        await loadHistory(null, getGuestId())
        return
      }
      globalSyncInProgress = true
      try {
        const guestId = getGuestId()
        await syncSearchHistoryLogout({ userId: uid, guestId })
        lastUserId.current = null
        await loadHistory(null, guestId, true)
      } finally {
        globalSyncInProgress = false
      }
    }

    const init = async () => {
      const supabase = getSearchHistoryClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()
      const uid = session?.user?.id ?? null
      if (uid) {
        lastUserId.current = uid
        await handleLogin(uid)
      } else {
        await loadHistory(null, getGuestId())
        setIsInitialized(true)
      }
    }

    init().catch(() => {})

    const { data: listener } = getSearchHistoryClient().auth.onAuthStateChange(
      (event: AuthChangeEvent, session: Session | null) => {
        if (event === "SIGNED_IN") {
          handleLogin(session?.user?.id ?? null).catch(() => {})
        } else if (event === "SIGNED_OUT") {
          handleLogout().catch(() => {})
        }
      }
    )

    return () => {
      listener.subscription.unsubscribe()
    }
  }, [])

  const addToHistory = useCallback(async (term: string) => {
    const trimmed = term.trim()
    if (!trimmed) return

    const normalized = normalizeTerm(trimmed)
    const base = globalHistoryLoaded ? globalHistory : searchHistory
    const filtered = base.filter((item) => normalizeTerm(item) !== normalized)
    const next = [trimmed, ...filtered].slice(0, MAX_HISTORY_ITEMS)

    broadcastHistory(next)

    const guestId = getGuestId()
    const userId = await resolveUserId(lastUserId)
    try {
      await postSearchHistoryTerm({ term: trimmed, userId, guestId })
    } catch {
      await loadHistory(userId, guestId, true)
    }
  }, [searchHistory])

  const removeFromHistory = useCallback(async (term: string) => {
    const trimmed = term.trim()
    if (!trimmed) return

    const normalized = normalizeTerm(trimmed)
    const base = globalHistoryLoaded ? globalHistory : searchHistory
    const next = base.filter((item) => normalizeTerm(item) !== normalized)
    broadcastHistory(next)

    const guestId = getGuestId()
    const userId = await resolveUserId(lastUserId)
    try {
      await deleteSearchHistoryTerm({ term: trimmed, userId, guestId })
    } catch {
      await loadHistory(userId, guestId, true)
    }
  }, [searchHistory])

  const clearHistory = useCallback(async () => {
    broadcastHistory([])
    const guestId = getGuestId()
    const userId = await resolveUserId(lastUserId)
    try {
      await clearSearchHistoryApi({ userId, guestId })
    } catch {
      await loadHistory(userId, guestId, true)
    }
  }, [])

  // B2 (demand-funnel instrumentation) — record how many listings `term`'s
  // search returned, once the caller knows it (results settle AFTER the
  // term is committed to history, so this is a separate call — see
  // components/search/unified-search-page-client.tsx). Does NOT touch the
  // visible history list (no broadcastHistory) — this only updates
  // search_history.result_count on the same upserted row.
  const recordResultCount = useCallback(async (term: string, resultCount: number) => {
    const trimmed = term.trim()
    if (!trimmed) return

    const guestId = getGuestId()
    const userId = await resolveUserId(lastUserId)
    try {
      await postSearchHistoryTerm({ term: trimmed, userId, guestId, resultCount })
    } catch {
      // Best-effort — zero-result-rate is a trend metric, not user-facing.
    }
  }, [])

  return {
    searchHistory,
    addToHistory,
    removeFromHistory,
    clearHistory,
    isInitialized,
    recordResultCount,
  }
}

